import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { fmtAED } from '../lib/format';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../components/ui/table';
import { Sparkles, Send, TrendingUp, Loader2, Percent, Download, FileText } from 'lucide-react';
import { toast } from 'sonner';
import PageHelp from '../components/PageHelp';

const SUGGESTED = [
  'What was my best-selling item this week?',
  'Are there any anomalies in sales or voids?',
  'Which day had the highest revenue and why?',
  'Give me 3 recommendations to increase revenue.',
];

const DETAILED_REPORTS = [
  { key: 'daily-summary', label: 'Daily Sales Summary' },
  { key: 'sales-detail', label: 'Sales Detail (every bill)' },
  { key: 'item-sales', label: 'Item Sales' },
  { key: 'payment-summary', label: 'Payment Summary' },
  { key: 'order-type-sales', label: 'Order Type Sales' },
  { key: 'discount-report', label: 'Discount / Complimentary' },
  { key: 'void-report', label: 'Cancelled / Voided' },
  { key: 'shift-closing', label: 'Cashier / Shift Closing' },
  { key: 'vat-summary', label: 'VAT / Tax Summary' },
  { key: 'expense-report', label: 'Expense Report' },
  { key: 'stock-summary', label: 'Stock Summary' },
  { key: 'stock-movement', label: 'Stock Movement' },
  { key: 'wastage-report', label: 'Wastage / Spoilage' },
  { key: 'purchase-report', label: 'Purchase / Goods Received' },
  { key: 'profit-summary', label: 'Profit / Food Cost Summary' },
  { key: 'audit-report', label: 'Audit Log' },
];

const LIST_COLUMNS = {
  'item-sales': [['name', 'Item'], ['category', 'Category'], ['qty', 'Qty Sold', 'num'], ['value', 'Selling Value', 'money']],
  'discount-report': [['invoice_no', 'Invoice'], ['date', 'Date'], ['kind', 'Kind'], ['discount', 'Discount', 'money'], ['note', 'Note/Reason'], ['cashier', 'Cashier']],
  'void-report': [['order_no', 'Order'], ['date', 'Date'], ['amount', 'Amount', 'money'], ['reason', 'Reason'], ['employee', 'Employee']],
  'stock-summary': [['name', 'Item'], ['unit', 'Unit'], ['opening', 'Opening (est.)', 'num'], ['received', 'Received', 'num'], ['consumed', 'Consumed', 'num'], ['adjustments', 'Adjustments', 'num'], ['closing', 'Closing', 'num']],
  'stock-movement': [['date', 'Date'], ['item', 'Item'], ['change', 'Change', 'num'], ['unit', 'Unit'], ['reason', 'Reason']],
  'wastage-report': [['date', 'Date'], ['item', 'Item'], ['qty', 'Qty', 'num'], ['cost', 'Cost', 'money'], ['reason', 'Reason'], ['employee', 'Employee']],
  'purchase-report': [['po_no', 'PO'], ['supplier', 'Supplier'], ['item_count', 'Items', 'num'], ['total_qty', 'Qty', 'num'], ['value', 'Value', 'money'], ['status', 'Status'], ['date', 'Date']],
  'audit-report': [['at', 'Time'], ['user_name', 'User'], ['role', 'Role'], ['action', 'Action'], ['entity', 'Entity'], ['details', 'Details']],
};
const todayStr = () => new Date().toISOString().slice(0, 10);

function DetailedReports({ currentOutlet }) {
  const [rpt, setRpt] = useState('daily-summary');
  const [from, setFrom] = useState(todayStr());
  const [to, setTo] = useState(todayStr());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const params = { outlet_id: currentOutlet?.id };
        if (rpt === 'daily-summary') params.date = from;
        else { params.start = from; params.end = to; }
        const { data } = await api.get(`/reports/${rpt}`, { params });
        setData(data);
      } catch (e) { setData(null); toast.error('Could not load report'); }
      finally { setLoading(false); }
    })();
  }, [rpt, from, to, currentOutlet]);

  const doExport = async (fmt) => {
    setExporting(fmt);
    try {
      const params = { report: rpt, fmt, outlet_id: currentOutlet?.id };
      if (rpt === 'daily-summary') params.date = from;
      else { params.start = from; params.end = to; }
      const res = await api.get('/reports/export', { params, responseType: 'blob' });
      const cd = res.headers?.['content-disposition'] || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const fname = m ? m[1] : `robochef-${rpt}-${from}_${to}.${fmt}`;
      const url = URL.createObjectURL(new Blob([res.data], { type: fmt === 'pdf' ? 'application/pdf' : 'text/csv' }));
      const a = document.createElement('a'); a.href = url; a.download = fname; a.click(); URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded · ${fname}`);
    } catch (e) { toast.error('Export failed'); }
    finally { setExporting(null); }
  };

  const KV = ({ rows }) => (
    <Table><TableBody>{rows.map(([k, v]) => (
      <TableRow key={k} data-testid={`rpt-kv-${k}`}><TableCell className="text-muted-foreground">{k}</TableCell><TableCell className="text-right font-mono font-medium">{v}</TableCell></TableRow>
    ))}</TableBody></Table>
  );

  const ModeTable = ({ obj, head, testPrefix }) => (
    <Table>
      <TableHeader><TableRow><TableHead>{head}</TableHead><TableHead className="text-right">Count</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
      <TableBody>{Object.entries(obj || {}).map(([k, v]) => (
        <TableRow key={k} data-testid={`${testPrefix}-${k}`}><TableCell className="capitalize">{k.replace(/_/g, ' ')}</TableCell><TableCell className="text-right font-mono">{v.count}</TableCell><TableCell className="text-right font-mono">{fmtAED(v.amount ?? v.sales)}</TableCell></TableRow>
      ))}</TableBody>
    </Table>
  );

  const ListTable = ({ cols, rows }) => (
    <Card className="overflow-x-auto"><Table>
      <TableHeader><TableRow>{cols.map(([, label, type]) => (
        <TableHead key={label} className={type ? 'text-right' : ''}>{label}</TableHead>))}</TableRow></TableHeader>
      <TableBody>{rows.map((r, i) => (
        <TableRow key={i} data-testid={`${rpt}-row-${i}`}>
          {cols.map(([k, label, type]) => (
            <TableCell key={label} className={`capitalize ${type ? 'text-right font-mono' : ''}`}>
              {type === 'money' ? fmtAED(r[k]) : (k === 'details' ? JSON.stringify(r[k] || {}) : String(r[k] ?? '—'))}
            </TableCell>
          ))}
        </TableRow>
      ))}</TableBody></Table>
      {rows.length === 0 && <div className="p-8 text-center text-muted-foreground">No records in this period</div>}
    </Card>
  );

  const renderBody = () => {
    if (loading) return <div className="p-8 text-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>;
    if (!data) return <div className="p-8 text-center text-muted-foreground">No data for this period</div>;
    if (LIST_COLUMNS[rpt]) return <ListTable cols={LIST_COLUMNS[rpt]} rows={Array.isArray(data) ? data : []} />;
    if (rpt === 'shift-closing') return (<div className="space-y-3">
      <div className="text-xs font-semibold text-muted-foreground">BY DAY</div>
      <ListTable cols={[['date', 'Date'], ['cash_sales', 'Cash Sales', 'money'], ['other_sales', 'Other Collections', 'money'], ['cash_expenses', 'Cash Expenses', 'money'], ['expected_closing_cash', 'Expected Closing Cash', 'money']]} rows={data.by_date || []} />
      <div className="text-xs font-semibold text-muted-foreground">BY CASHIER</div>
      <ListTable cols={[['cashier', 'Cashier'], ['bills', 'Bills', 'num'], ['total_collected', 'Total Collected', 'money'], ['cash_collected', 'Cash Collected', 'money']]} rows={data.by_cashier || []} />
    </div>);
    switch (rpt) {
      case 'daily-summary':
        return <KV rows={[['Gross Sales', fmtAED(data.gross_sales)], ['Discounts', fmtAED(data.discounts)], ['VAT (5%)', fmtAED(data.vat)], ['Net Sales', fmtAED(data.net_sales)], ['No. of Bills', data.bill_count], ['Average Bill', fmtAED(data.average_bill)]]} />;
      case 'vat-summary':
        return <KV rows={[['Taxable Amount', fmtAED(data.taxable_amount)], ['VAT Amount', fmtAED(data.vat_amount)], ['Tax Mode', data.tax_mode], ['VAT Rate', `${(data.vat_rate * 100).toFixed(0)}%`], ['Total Sales (incl. VAT)', fmtAED(data.total_sales)], ['Bills', data.bill_count]]} />;
      case 'profit-summary':
        return <KV rows={[['Sales', fmtAED(data.sales)], ['Estimated COGS (food cost)', fmtAED(data.cogs)], ['Gross Profit', fmtAED(data.gross_profit)], ['Gross Margin', `${data.gross_margin_pct}%`]]} />;
      case 'payment-summary':
        return (<div className="space-y-3">
          <ModeTable obj={data.modes} head="Payment Method" testPrefix="pay-sum" />
          <p className="text-sm text-muted-foreground">Split payments: <b>{data.split_bills}</b> bill(s) · {fmtAED(data.split_amount)}</p>
        </div>);
      case 'order-type-sales':
        return (<div className="space-y-3">
          <ModeTable obj={data.by_type} head="Order Type" testPrefix="otype-sum" />
          <div className="text-xs font-semibold text-muted-foreground pt-2">BY CHANNEL</div>
          <ModeTable obj={data.by_channel} head="Channel" testPrefix="channel-sum" />
        </div>);
      case 'sales-detail': {
        const rows = Array.isArray(data) ? data : [];
        return (<Card className="overflow-x-auto"><Table>
          <TableHeader><TableRow><TableHead>Invoice</TableHead><TableHead>Date</TableHead><TableHead>Time</TableHead><TableHead>Type</TableHead><TableHead>Cashier</TableHead><TableHead>Payment</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
          <TableBody>{rows.map((r) => (
            <TableRow key={r.order_no} data-testid={`sales-detail-${r.order_no}`}>
              <TableCell className="font-mono">{r.invoice_no || r.order_no}</TableCell>
              <TableCell>{r.date}</TableCell><TableCell>{r.time}</TableCell>
              <TableCell className="capitalize">{r.type}</TableCell><TableCell>{r.cashier}</TableCell>
              <TableCell className="capitalize text-xs">{r.payment_methods.join(' + ')}</TableCell>
              <TableCell><Badge variant="secondary">{r.payment_status}</Badge></TableCell>
              <TableCell className="text-right font-mono">{fmtAED(r.total)}</TableCell>
            </TableRow>
          ))}</TableBody></Table>
          {rows.length === 0 && <div className="p-8 text-center text-muted-foreground">No bills in this period</div>}
        </Card>);
      }
      case 'expense-report':
        return (<div className="space-y-3">
          <ModeTable obj={data.by_category} head="Category" testPrefix="exp-cat" />
          <div className="text-xs font-semibold text-muted-foreground pt-2">DETAIL — total {fmtAED(data.total)}</div>
          <Card className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Category</TableHead><TableHead>Mode</TableHead><TableHead>Note</TableHead><TableHead>Employee</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
            <TableBody>{(data.rows || []).map((r, i) => (
              <TableRow key={i}><TableCell>{r.date}</TableCell><TableCell>{r.category}</TableCell><TableCell className="capitalize">{r.payment_mode}</TableCell><TableCell className="text-muted-foreground">{r.note}</TableCell><TableCell>{r.employee}</TableCell><TableCell className="text-right font-mono">{fmtAED(r.amount)}</TableCell></TableRow>
            ))}</TableBody></Table></Card>
        </div>);
      default:
        return null;
    }
  };

  return (
    <div className="space-y-3" data-testid="detailed-reports">
      <div className="flex items-end gap-2 flex-wrap">
        <div>
          <div className="text-xs text-muted-foreground mb-1">Report</div>
          <select className="h-9 rounded-md border bg-background px-2 min-w-56" data-testid="report-selector" value={rpt} onChange={(e) => { setRpt(e.target.value); setData(null); }}>
            {DETAILED_REPORTS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
          </select>
        </div>
        <div><div className="text-xs text-muted-foreground mb-1">{rpt === 'daily-summary' ? 'Date' : 'From'}</div><Input type="date" className="h-9 w-40" data-testid="report-from" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
        {rpt !== 'daily-summary' && <div><div className="text-xs text-muted-foreground mb-1">To</div><Input type="date" className="h-9 w-40" data-testid="report-to" value={to} onChange={(e) => setTo(e.target.value)} /></div>}
        <div className="flex gap-2 ml-auto">
          <Button variant="outline" size="sm" data-testid="report-export-csv" disabled={!!exporting} onClick={() => doExport('csv')}>
            {exporting === 'csv' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} CSV
          </Button>
          <Button variant="outline" size="sm" data-testid="report-export-pdf" disabled={!!exporting} onClick={() => doExport('pdf')}>
            {exporting === 'pdf' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <FileText className="h-4 w-4 mr-1" />} PDF
          </Button>
        </div>
      </div>
      {renderBody()}
    </div>
  );
}

export default function Reports() {
  const { currentOutlet, can } = useAuth();
  const [sales, setSales] = useState(null);
  const [q, setQ] = useState('');
  const [chat, setChat] = useState([]);
  const [loading, setLoading] = useState(false);
  const [menuEng, setMenuEng] = useState(null);
  const [mLoading, setMLoading] = useState(false);
  const [costing, setCosting] = useState(null);

  useEffect(() => { (async () => { const { data } = await api.get('/reports/sales'); setSales(data); })(); }, []);
  const loadCosting = async () => { try { const { data } = await api.get('/menu/costing'); setCosting(data); } catch (e) {} };

  const ask = async (question) => {
    const question2 = question || q;
    if (!question2.trim()) return;
    setChat((c) => [...c, { role: 'user', text: question2 }]);
    setQ(''); setLoading(true);
    try {
      const { data } = await api.post('/ai/insights', { question: question2, outlet_id: currentOutlet?.id });
      setChat((c) => [...c, { role: 'ai', data }]);
    } catch (e) {
      setChat((c) => [...c, { role: 'ai', data: { answer: 'AI request failed.', error: true } }]);
    } finally { setLoading(false); }
  };

  const runMenuEng = async () => { setMLoading(true); try { const { data } = await api.get('/ai/menu-engineering'); setMenuEng(data); } catch (e) {} finally { setMLoading(false); } };

  const catBadge = { star: 'bg-success text-success-foreground', plowhorse: 'bg-warning text-warning-foreground', puzzle: 'bg-primary text-primary-foreground', dog: 'bg-destructive text-destructive-foreground' };

  return (
    <div className="p-4 md:p-6 space-y-4">
      <PageHelp id="reports">Ask the <b>AI assistant</b> plain-English questions about your sales (powered by DeepSeek). Use the <b>Sales</b> tab for VAT-ready figures and <b>Menu Engineering</b> for AI classification of your dishes.</PageHelp>
      <h1 className="font-display text-2xl font-bold">Reports & AI Insights</h1>
      <Tabs defaultValue="ai">
        <TabsList>
          <TabsTrigger value="ai" data-testid="tab-ai"><Sparkles className="h-4 w-4 mr-1" />AI Insights</TabsTrigger>
          <TabsTrigger value="sales">Sales</TabsTrigger>
          <TabsTrigger value="detailed" data-testid="tab-detailed">Detailed</TabsTrigger>
          <TabsTrigger value="food-cost" data-testid="tab-food-cost" onClick={loadCosting}><Percent className="h-4 w-4 mr-1" />Food Cost</TabsTrigger>
          <TabsTrigger value="menu-eng" data-testid="tab-menu-eng">Menu Engineering</TabsTrigger>
        </TabsList>

        <TabsContent value="ai" className="pt-3">
          <Card className="p-4 flex flex-col" style={{ minHeight: 460 }}>
            <div className="flex-1 space-y-3 overflow-y-auto mb-3">
              {chat.length === 0 && (
                <div className="text-center text-muted-foreground py-8">
                  <Sparkles className="h-10 w-10 mx-auto mb-2 text-primary" />
                  <p className="mb-4">Ask anything about your restaurant’s performance.</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {SUGGESTED.map((s) => <button key={s} data-testid={`ai-suggested`} onClick={() => ask(s)} className="text-xs px-3 py-1.5 rounded-full bg-muted hover:bg-accent">{s}</button>)}
                  </div>
                </div>
              )}
              {chat.map((m, i) => m.role === 'user' ? (
                <div key={i} className="flex justify-end"><div className="bg-primary text-primary-foreground rounded-2xl rounded-tr-sm px-4 py-2 max-w-[80%] text-sm">{m.text}</div></div>
              ) : (
                <div key={i} className="flex justify-start"><div className="bg-muted rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%] text-sm space-y-2">
                  <div>{m.data.answer}</div>
                  {m.data.highlights?.length > 0 && <ul className="list-disc pl-4 text-xs text-muted-foreground space-y-0.5">{m.data.highlights.map((h, j) => <li key={j}>{h}</li>)}</ul>}
                  {m.data.anomaly && <div className="text-xs text-destructive"><b>Anomaly:</b> {m.data.anomaly}</div>}
                  {m.data.recommendation && <div className="text-xs text-primary"><b>Recommendation:</b> {m.data.recommendation}</div>}
                </div></div>
              ))}
              {loading && <div className="flex justify-start"><div className="bg-muted rounded-2xl px-4 py-3"><Loader2 className="h-4 w-4 animate-spin" /></div></div>}
            </div>
            <div className="flex gap-2">
              <Input data-testid="ai-prompt-input" placeholder="Ask about sales, items, trends…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && ask()} />
              <Button data-testid="ai-send-button" onClick={() => ask()} disabled={loading}><Send className="h-4 w-4" /></Button>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="sales" className="pt-3 space-y-3">
          {sales && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Card className="p-4"><div className="text-sm text-muted-foreground">Gross Sales</div><div className="text-xl font-display font-bold">{fmtAED(sales.gross)}</div></Card>
              <Card className="p-4"><div className="text-sm text-muted-foreground">Orders</div><div className="text-xl font-display font-bold">{sales.count}</div></Card>
              <Card className="p-4"><div className="text-sm text-muted-foreground">VAT Collected</div><div className="text-xl font-display font-bold">{fmtAED(sales.vat_collected)}</div></Card>
              <Card className="p-4"><div className="text-sm text-muted-foreground">Payment Modes</div><div className="text-xs mt-1">{Object.entries(sales.payment_modes).map(([k, v]) => <div key={k} className="flex justify-between capitalize"><span>{k}</span><span className="font-mono">{fmtAED(v)}</span></div>)}</div></Card>
            </div>
          )}
          <Card className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Subtotal</TableHead><TableHead className="text-right">VAT</TableHead><TableHead className="text-right">Total</TableHead></TableRow></TableHeader>
            <TableBody>{(sales?.orders || []).slice(0, 50).map((o) => (
              <TableRow key={o.id}><TableCell className="font-mono">{o.order_no}</TableCell><TableCell className="capitalize">{o.type}</TableCell><TableCell className="text-right font-mono">{fmtAED(o.subtotal)}</TableCell><TableCell className="text-right font-mono">{fmtAED(o.vat_total)}</TableCell><TableCell className="text-right font-mono">{fmtAED(o.grand_total)}</TableCell></TableRow>
            ))}</TableBody>
          </Table></Card>
        </TabsContent>

        <TabsContent value="detailed" className="pt-3"><DetailedReports currentOutlet={currentOutlet} /></TabsContent>

        <TabsContent value="food-cost" className="pt-3 space-y-3">
          <p className="text-sm text-muted-foreground">Internal cost & margin per item (not shown on the POS/menu cards). Food Cost % above 35% is highlighted.</p>
          <Card className="overflow-x-auto"><Table>
            <TableHeader><TableRow><TableHead>Item</TableHead><TableHead className="text-right">Price</TableHead><TableHead className="text-right">Cost</TableHead><TableHead className="text-right">Food Cost %</TableHead><TableHead className="text-right">Margin</TableHead></TableRow></TableHeader>
            <TableBody>{(costing || []).map((c) => (
              <TableRow key={c.id} data-testid={`food-cost-row-${c.name}`}>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-right font-mono">{fmtAED(c.price)}</TableCell>
                <TableCell className="text-right font-mono">{fmtAED(c.cost)}{c.has_options ? '+' : ''}</TableCell>
                <TableCell className="text-right font-mono"><Badge variant="outline" className={c.food_cost_pct > 35 ? 'text-destructive border-destructive/40' : 'text-success border-success/40'}>{c.food_cost_pct}%</Badge></TableCell>
                <TableCell className="text-right font-mono">{fmtAED(c.margin)}</TableCell>
              </TableRow>
            ))}</TableBody>
          </Table></Card>
          {costing && costing.length === 0 && <div className="p-6 text-center text-muted-foreground">No costing data — add recipes to items in Menu.</div>}
        </TabsContent>

        <TabsContent value="menu-eng" className="pt-3 space-y-3">
          <Button data-testid="run-menu-eng" onClick={runMenuEng} disabled={mLoading}>{mLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <TrendingUp className="h-4 w-4 mr-1" />}Analyze Menu with AI</Button>
          {menuEng?.summary && <Card className="p-4 text-sm">{menuEng.summary}</Card>}
          {menuEng?.classifications && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {menuEng.classifications.map((c, i) => (
                <Card key={i} className="p-4"><div className="flex items-center justify-between"><div className="font-medium">{c.name}</div><Badge className={catBadge[c.category] || ''}>{c.category}</Badge></div><div className="text-xs text-muted-foreground mt-2">{c.advice}</div></Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
